/**
 * Project Detail Page - FLO SIGNATURE DESIGN SYSTEM v2
 * Clean. Controlled. Consistent. Light ops mode.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiClient } from '../api/apiConfig';
import { Project } from '../types/project';
import ProjectForm from '../components/forms/ProjectForm';
import RelatedItemsTabs from '../components/ui/RelatedItemsTabs';
import JournalForm from '../components/forms/JournalForm';
import { useJournalModal } from '../hooks/useJournalModal';
import { PencilIcon, TrashIcon, ArrowPathIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { COLORS, SPACING, TYPOGRAPHY } from '../design/tokens';
import { Panel, Button, Badge, Divider, Textarea } from '../design/primitives';

const ProjectDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [updatingTaskId, setUpdatingTaskId] = useState<number | null>(null);
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [editedNotes, setEditedNotes] = useState('');
  const [isSavingNotes, setIsSavingNotes] = useState(false);

  const fetchProject = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get(`/api/projects/${id}/`);
      setProject(response.data);
    } catch (err) {
      setError('Failed to load project details.');
      console.error('Error fetching project:', err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const {
    isJournalModalOpen,
    selectedJournalEntry,
    handleJournalEntryClick,
    handleJournalEntryUpdated,
    handleCloseJournal,
  } = useJournalModal(fetchProject);

  useEffect(() => {
    if (id) {
      fetchProject();
    }
  }, [id, fetchProject]);

  const handleTaskToggle = async (taskId: number, currentStatus: boolean) => {
    setUpdatingTaskId(taskId);
    try {
      await apiClient.patch(`/api/tasks/${taskId}/`, { is_done: !currentStatus });
      await fetchProject();
    } catch (err) {
      console.error('Failed to update task:', err);
    } finally {
      setUpdatingTaskId(null);
    }
  };

  const handleProjectUpdated = () => {
    setIsEditModalOpen(false);
    fetchProject();
  };

  const handleDelete = async () => {
    if (!id) return;
    if (!window.confirm('Are you sure you want to delete this project?')) return;
    try {
      await apiClient.delete(`/api/projects/${id}/`);
      navigate('/projects');
    } catch (err) {
      alert('Failed to delete project.');
    }
  };

  const handleEditNotes = () => {
    setEditedNotes(project?.notes || '');
    setIsEditingNotes(true);
  };

  const handleCancelEditNotes = () => {
    setIsEditingNotes(false);
    setEditedNotes('');
  };

  const handleSaveNotes = async () => {
    if (!id) return;
    setIsSavingNotes(true);
    try {
      await apiClient.patch(`/api/projects/${id}/`, { notes: editedNotes });
      await fetchProject();
      setIsEditingNotes(false);
    } catch (err) {
      alert('Failed to save notes.');
      console.error('Error saving notes:', err);
    } finally {
      setIsSavingNotes(false);
    }
  };

  const getStatusBadgeColor = (status: string): 'success' | 'warning' | 'default' => {
    switch (status) {
      case 'active':
        return 'success';
      case 'paused':
        return 'warning';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <div style={{
        backgroundColor: COLORS.bg.base,
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: SPACING[24]
      }}>
        <ArrowPathIcon style={{
          width: '24px',
          height: '24px',
          color: COLORS.text.tertiary,
          animation: 'spin 1s linear infinite'
        }} />
      </div>
    );
  }

  if (error || !project) {
    return (
      <div style={{
        backgroundColor: COLORS.bg.base,
        minHeight: '100vh',
        padding: SPACING[24]
      }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <Panel style={{ textAlign: 'center', padding: SPACING[48] }}>
            <ExclamationTriangleIcon style={{
              width: '48px',
              height: '48px',
              color: COLORS.status.error,
              margin: '0 auto',
              marginBottom: SPACING[16]
            }} />
            <h2 style={{
              fontFamily: TYPOGRAPHY.headings.fontFamily,
              fontSize: TYPOGRAPHY.headings.sizes.h3,
              fontWeight: TYPOGRAPHY.headings.weight,
              color: COLORS.text.primary,
              margin: 0,
              marginBottom: SPACING[8]
            }}>
              Error
            </h2>
            <p style={{
              fontFamily: TYPOGRAPHY.body.fontFamily,
              fontSize: TYPOGRAPHY.body.sizes.default,
              color: COLORS.text.secondary,
              margin: 0,
              marginBottom: SPACING[16]
            }}>
              {error || "Project not found"}
            </p>
            <button
              onClick={() => navigate('/projects')}
              style={{
                background: 'none',
                border: 'none',
                color: COLORS.accent.default,
                fontFamily: TYPOGRAPHY.body.fontFamily,
                fontSize: TYPOGRAPHY.body.sizes.default,
                cursor: 'pointer',
                padding: 0
              }}
              onMouseEnter={(e) => e.currentTarget.style.color = COLORS.accent.hover}
              onMouseLeave={(e) => e.currentTarget.style.color = COLORS.accent.default}
            >
              Return to Projects
            </button>
          </Panel>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      backgroundColor: COLORS.bg.base,
      minHeight: '100vh',
      padding: SPACING[24]
    }}>
      <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
        {/* Header */}
        <Panel style={{ marginBottom: SPACING[24] }}>
          <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            marginBottom: SPACING[16]
          }}>
            <div style={{ flex: 1 }}>
              <h1 style={{
                fontFamily: TYPOGRAPHY.headings.fontFamily,
                fontSize: TYPOGRAPHY.headings.sizes.h1,
                fontWeight: TYPOGRAPHY.headings.weight,
                color: COLORS.text.primary,
                margin: 0,
                marginBottom: SPACING[8]
              }}>
                {project.name}
              </h1>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: SPACING[16]
              }}>
                <Badge color={getStatusBadgeColor(project.status || 'active')}>
                  {project.status}
                </Badge>
                {project.type && (
                  <span style={{
                    fontFamily: TYPOGRAPHY.body.fontFamily,
                    fontSize: TYPOGRAPHY.body.sizes.default,
                    color: COLORS.text.secondary
                  }}>
                    Type: {project.type}
                  </span>
                )}
                {project.income_monthly && project.income_monthly > 0 && (
                  <span style={{
                    fontFamily: TYPOGRAPHY.body.fontFamily,
                    fontSize: TYPOGRAPHY.body.sizes.default,
                    color: COLORS.text.secondary
                  }}>
                    Monthly Income: ${project.income_monthly}
                  </span>
                )}
              </div>
            </div>
            <div style={{ display: 'flex', gap: SPACING[8] }}>
              <Button
                variant="secondary"
                onClick={() => setIsEditModalOpen(true)}
                icon={<PencilIcon style={{ width: '16px', height: '16px' }} />}
              >
                Edit
              </Button>
              <Button
                variant="danger"
                onClick={handleDelete}
                icon={<TrashIcon style={{ width: '16px', height: '16px' }} />}
              >
                Delete
              </Button>
            </div>
          </div>

          {/* Description / Notes */}
          <Divider />

          <div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: SPACING[8]
            }}>
              <h3 style={{
                fontFamily: TYPOGRAPHY.body.fontFamily,
                fontSize: TYPOGRAPHY.body.sizes.small,
                fontWeight: '600',
                color: COLORS.text.secondary,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                margin: 0
              }}>
                Description
              </h3>
              {!isEditingNotes && (
                <button
                  onClick={handleEditNotes}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: SPACING[4],
                    background: 'none',
                    border: 'none',
                    color: COLORS.accent.default,
                    fontFamily: TYPOGRAPHY.body.fontFamily,
                    fontSize: TYPOGRAPHY.body.sizes.small,
                    cursor: 'pointer',
                    padding: 0
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.color = COLORS.accent.hover}
                  onMouseLeave={(e) => e.currentTarget.style.color = COLORS.accent.default}
                >
                  <PencilIcon style={{ width: '12px', height: '12px' }} />
                  Edit
                </button>
              )}
            </div>

            {isEditingNotes ? (
              <div>
                <div style={{ marginBottom: SPACING[8] }}>
                  <Textarea
                    value={editedNotes}
                    onChange={(e) => setEditedNotes(e.target.value)}
                    rows={6}
                    placeholder="Detailed description including purpose, key people, keywords (e.g., painting, flooring, website, SaaS) - helps AI classify emails/meetings"
                  />
                </div>
                <div style={{ display: 'flex', gap: SPACING[8] }}>
                  <Button
                    variant="primary"
                    onClick={handleSaveNotes}
                    disabled={isSavingNotes}
                  >
                    {isSavingNotes ? 'Saving...' : 'Save'}
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={handleCancelEditNotes}
                    disabled={isSavingNotes}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div>
                {project.notes ? (
                  <p style={{
                    fontFamily: TYPOGRAPHY.body.fontFamily,
                    fontSize: TYPOGRAPHY.body.sizes.default,
                    color: COLORS.text.primary,
                    whiteSpace: 'pre-wrap',
                    margin: 0,
                    lineHeight: '1.6'
                  }}>
                    {project.notes}
                  </p>
                ) : (
                  <p style={{
                    fontFamily: TYPOGRAPHY.body.fontFamily,
                    fontSize: TYPOGRAPHY.body.sizes.default,
                    color: COLORS.text.tertiary,
                    fontStyle: 'italic',
                    margin: 0
                  }}>
                    No description yet. Click Edit to add one.
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Business and Categories */}
          {(project.business || (project.categories && project.categories.length > 0)) && (
            <>
              <Divider />
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: SPACING[16]
              }}>
                {project.business && (
                  <div>
                    <span style={{
                      fontFamily: TYPOGRAPHY.body.fontFamily,
                      fontSize: TYPOGRAPHY.body.sizes.default,
                      fontWeight: '600',
                      color: COLORS.text.secondary,
                      marginRight: SPACING[8]
                    }}>
                      Business:
                    </span>
                    <span style={{
                      fontFamily: TYPOGRAPHY.body.fontFamily,
                      fontSize: TYPOGRAPHY.body.sizes.default,
                      color: COLORS.text.primary
                    }}>
                      {project.business.name}
                    </span>
                  </div>
                )}
                {project.categories && project.categories.length > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: SPACING[8] }}>
                    <span style={{
                      fontFamily: TYPOGRAPHY.body.fontFamily,
                      fontSize: TYPOGRAPHY.body.sizes.default,
                      fontWeight: '600',
                      color: COLORS.text.secondary
                    }}>
                      Categories:
                    </span>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: SPACING[4] }}>
                      {project.categories.map((category) => (
                        <Badge key={category.id} color="accent">
                          {category.name}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </Panel>

        {/* Related Items Tabs */}
        <RelatedItemsTabs
          tasks={project.task_set}
          emails={project.emails}
          meetings={project.meetings}
          journalEntries={project.journal_entries}
          people={project.people}
          recordings={project.recordings}
          onTaskToggle={handleTaskToggle}
          onJournalEntryClick={handleJournalEntryClick}
          isUpdatingTask={updatingTaskId}
        />

        {isEditModalOpen && (
          <ProjectForm
            isOpen={isEditModalOpen}
            onClose={() => setIsEditModalOpen(false)}
            initialProject={project}
            isEditMode={true}
            onProjectUpdated={handleProjectUpdated}
          />
        )}

        {isJournalModalOpen && selectedJournalEntry && (
          <JournalForm
            isOpen={isJournalModalOpen}
            onClose={handleCloseJournal}
            onJournalEntryCreated={handleJournalEntryUpdated}
            onJournalEntryUpdated={handleJournalEntryUpdated}
            initialEntry={selectedJournalEntry}
            isEditMode={true}
          />
        )}
      </div>
    </div>
  );
};

export default ProjectDetailPage;
